import React, { useMemo, useEffect, useRef, useState } from 'react';

import {
  Autocomplete, Box, Typography, Grid, Divider, Button, Checkbox, RadioGroup, Radio, FormControlLabel
} from '@mui/material';
import {
  DataGrid, GridActionsCellItem, GridRowModes
} from '@mui/x-data-grid';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { styled } from "@mui/system";
import MuiTextField from "@mui/material/TextField";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useAmortization from '../hooks/useAmortization';
import dayjs from 'dayjs';
import { format, parse } from 'date-fns';
import * as Yup from "yup";
import { useFormik, FormikProvider, Field, Form } from 'formik';
import { useFormikContext } from 'formik';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { esES } from '@mui/x-date-pickers';
import { ContactPageSharp } from '@mui/icons-material';
import { registerUpdateLifecycle } from 'echarts';
import { useCurrencyFieldAmor } from '../hooks/useCurrencyFieldAmor';
import { useCurrencyField } from '../hooks/useCurrencyField';
import PrintButton from 'app/components/PrintButton';
import { printAmortizationReport } from 'app/services/PrintService';

dayjs.extend(isSameOrAfter);

let BALLOON_DATE = null;

const PAYMENT_FREQUENCY_COUNTS = {
  1: 12,
  2: 4,
  3: 2,
  4: 1,
  5: 24,
  6: 26,
  7: 52,
};

const validationSchema = Yup.object().shape({
  amortization: Yup.object().shape({
    loanamount: Yup.number()
      .typeError('Loan Amount must be a number')
      .positive('Loan Amount must be a positive number')
      .required('You have to enter the total loan amount.'),
    monthsamortized: Yup.number()
      .typeError('Months Amortized must be a number'),
    paymentamount: Yup.number()
      .when('monthsamortized', {
        is: 0,
        then: () => Yup.number()
          .required('You have to enter payment amount when monthsamortized is 0.')
          .positive('Payment Amount must be a positive number')
          .typeError('Payment Amount must be a number'),
        otherwise: () => Yup.number().notRequired(),
      }),
    amor: Yup.boolean(),
    interestrate: Yup.number()
      .typeError('Interest Rate must be a number')
      .transform((value) => (value === null || value === 0 ? 0 : value)),
    paymentFrequency: Yup.number()
      .typeError('Payment Frequency must be a number')
      .required('Payment Frequency is required')
      .positive('Please select payments per year from the combo box.'),
    paymentDate1: Yup.number()
      .when(['paymentFrequency'], ([paymentFrequency], schema) => {
        return paymentFrequency === 5
          ? schema
            .required('Payment Date 1 is required for semi-monthly')
            .min(1, 'Must be a valid day')
            .max(31, 'Must be a valid day')
          : schema.notRequired();
      }),
    paymentDate2: Yup.number()
      .when(['paymentFrequency'], ([paymentFrequency], schema) => {
        return paymentFrequency === 5
          ? schema
            .required('Payment Date 2 is required for semi-monthly')
            .min(1, 'Must be a valid day')
            .max(31, 'Must be a valid day')
          : schema.notRequired();
      }),
    approvalDate: Yup.date()
      .required('Approval Date is required')
      .typeError('Approval Date must be a valid date'),
    firstDueDate: Yup.date()
      .required('First Due Date is required')
      .typeError('First Due Date must be a valid date')
      .test('is-after-note-date', 'The first payment due date is earlier than the note date.', function (value) {
        const { approvalDate } = this.parent;
        if (value && approvalDate) {
          return dayjs(value).isSameOrAfter(dayjs(approvalDate), 'day');
        }
        return true;
      })
      .test(
        'match-payment-date1-day',
        'Approval date day must match Payment Date 1',
        function (value) {
          const context = this.options.context || {};
          console.log(context);
          const paymentDate1 = context.amortization?.paymentDate1;
          const paymentFrequency = context.amortization?.paymentFrequency;

          console.log('paymentDate1:', paymentDate1);
          console.log('paymentFrequency:', paymentFrequency);

          if (paymentFrequency === 5 && value && paymentDate1) {
            const approvalDay = dayjs(value).date();
            const paymentDay = parseInt(paymentDate1, 10);
            return approvalDay === paymentDay;
          }

          return true;
        }
      ),
    maturityDate: Yup.date()
      .required('Maturity Date is required')
      .typeError('Maturity Date must be a valid date')
      .test('is-after-first-due-date', 'Maturity Date must be after the First Due Date.', function (value) {
        const { firstDueDate } = this.parent;
        if (value && firstDueDate) {
          return dayjs(value).isAfter(dayjs(firstDueDate), 'day') || dayjs(value).isSame(firstDueDate, 'day');
        }
        return true;
      })
  }),
});

const formatDate = (date) => format(new Date(date), 'MM/dd/yyyy');


const SmallStyledInput = styled(MuiTextField)({
  width: '110px !important',
  minWidth: '110px !important',
  '& label': { fontSize: '14px' },
  '& label.MuiInputLabel-shrink': { marginTop: '0px' },
  '& .MuiOutlinedInput-root': {
    '& .MuiOutlinedInput-input': { fontSize: '14px', padding: '10px 14px !important' }
  }
});

const StyledInput = styled(MuiTextField)({
  minWidth: "233px !important",
  "& label": { fontSize: "14px" },
  "& label.MuiInputLabel-shrink": { marginTop: "0px" },
  "& .MuiOutlinedInput-root": {
    "& .MuiOutlinedInput-input": { fontSize: "14px", padding: "10px 14px !important" }
  }
});

export const parseCurrency = (value) => {
  if (value == null || value === '') return 0;

  if (typeof value === 'number') return value;

  const cleaned = value.toString().replace(/[^0-9.-]+/g, '');
  const parsed = parseFloat(cleaned);

  return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (value) => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const StyledDatePicker = styled(DatePicker)({
  minWidth: "188px !important",
  "& .MuiOutlinedInput-root": {
    height: "40px",
    "& input": {
      padding: "10px 14px !important",
      fontSize: "14px",
    },
  },
  "& button": { padding: "6px" },
  "& label": { fontSize: "14px" },
});

const Amortization = ({ values, errors, touched, handleChange, handleBlur, setFieldValue, initialRows, loanTerms, readOnly, tabIndex }) => {
  const [rows, setRows] = useState(null);
  const paymentFrequencyTypingTimer = useRef(null);
  const today = dayjs().startOf('day');

  useEffect(() => () => clearTimeout(paymentFrequencyTypingTimer.current), []);

  const isDateError = (field) => formik.touched.amortization?.[field] && formik.errors.amortization?.[field];

  const formik = useFormik({
    initialValues: {
      amortization: {
        paymentFrequency: values?.amortization?.paymentFrequency || null,
        loanamount: values?.amortization?.loanamount || 0,
        monthsamortized: values?.amortization?.monthsamortized || 0,
        paymentamount: values?.amortization?.paymentamount || 0,
        amor: values?.amortization?.amor ?? true,
        daily: values?.amortization?.daily || false,
        dailyValues: values?.amortization?.dailyValues || 0,
        interestrate: values?.amortization?.interestrate || 0,
        noofpayments: values?.amortization?.noofpayments || 0,
        additionalsvcfee: values?.amortization?.additionalsvcfee || 0,
        totalpaymentwillbe: values?.amortization?.totalPayment || '0',
        approvalDate: values?.amortization?.approvalDate || today,
        firstDueDate: values?.amortization?.firstDueDate || today,
        maturityDate: values?.amortization?.maturityDate || today,
        scheduleList: [],
        paymentDate1: '',
        paymentDate2: '',
      }
    },
    validationSchema,
    onSubmit: (values) => {
      console.log('Form values:', values);
    },
  });

  // Total payment will be calculated based on payment and additional service fee
  useEffect(() => {
    const basePayment = parseFloat(formik.values.amortization.totalpaymentwillbe || 0);
    const additionalFee = parseFloat(formik.values.amortization.additionalsvcfee || 0);
    const total = basePayment + additionalFee;

    formik.setFieldValue('amortization.totalpaymentwillbe', total.toFixed(2));
  }, [formik.values.amortization.additionalsvcfee, formik.values.amortization?.basepayment]);


  // Function to calculate the payment
  const calculatePayment = (strM, APR, TotPmts, PVal, FVal) => {
    if (APR === 0) return 0;
    const rate = APR / strM;
    return (-Pmt(rate, TotPmts, PVal, FVal));
  };


  // The Pmt function calculates the payment amount per period
  const Pmt = (rate, nper, pv, fv) => {
    return (pv * rate) / (1 - Math.pow(1 + rate, -nper));
  };

  // The NPer function calculates the number of periods for a given payment
  const NPer = (rate, pmt, pv) => {
    return -Math.log(1 - (pv * rate / pmt)) / Math.log(1 + rate);
  };

  // const calculateNPer = (rate, pmt, pv) => {
  //     if (rate === 0) return pv / pmt;
  //     const numerator = Math.log(pmt / (pmt - pv * rate));
  //     const denominator = Math.log(1 + rate);
  //     return numerator / denominator;
  // };

  const calculateNPer = (rate, pmt, pv) => {
    if (rate === 0) return pv / pmt;

    const denominatorExpr = pmt - pv * rate;

    // Prevent invalid log operations
    if (denominatorExpr <= 0 || pmt <= 0 || pv <= 0 || rate <= 0) return NaN;

    const numerator = Math.log(pmt / denominatorExpr);
    const denominator = Math.log(1 + rate);

    return numerator / denominator;
  };

  const [showPaymentDates, setShowPaymentDates] = useState(false);

  const handlePaymentFrequencyBlur = (e, handleBlur) => {
    if (formik.values.amortization?.paymentFrequency == 5) {
      setShowPaymentDates(true);
    } else {
      setShowPaymentDates(false);
    }
    formik.handleBlur(e);
    console.log(formik.values.amortization.approvalDate);
    let approvalDate = formik.values.amortization.approvalDate;
    if (!dayjs.isDayjs(approvalDate)) {
      // If not, convert approvalDate to dayjs
      approvalDate = dayjs(approvalDate);
    }

    // original
    //calcFirstDueDate(formik.values.amortization.approvalDate);
    calcFirstDueDate(approvalDate);
  };



  const recalculatePayment = () => {
    const { loanamount, interestrate, monthsamortized, paymentFrequency, paymentamount, totalNumberOfPayments } = formik.values.amortization;
    console.log(paymentamount);
    const FVal = 0;
    const PVal = parseCurrency(loanamount);
    let APR = interestrate;
    if (APR > 0) APR = APR / 100;
    //let TotPmts = parseFloat(monthsamortized);
    let TotPmts = (!monthsamortized || parseInt(monthsamortized) === 0)
      ? parseFloat(totalNumberOfPayments)
      : parseFloat(monthsamortized);

    let intPP = paymentFrequency;
    let paymentAmt = parseCurrency(paymentamount);
    switch (paymentFrequency) {
      case 1: intPP = 12; break;
      case 5: TotPmts *= 2; intPP = 24; break;
      case 6: TotPmts = TotPmts / 12 * 26; intPP = 26; break;
      case 7: TotPmts = TotPmts / 12 * 52; intPP = 52; break;
      case 2: TotPmts = TotPmts / 3; intPP = 4; break;
      case 4: TotPmts = TotPmts / 12; intPP = 1; break;
      case 3: TotPmts = TotPmts / 6; intPP = 2; break;
      default: break;
    }

    let payment;
    if (!paymentAmt || paymentAmt == 0) {
      payment = calculatePayment(intPP, APR, TotPmts, PVal, FVal);
      console.log('Calculated Payment:', payment);
      if (payment < 0) payment = -payment;

      if (typeof payment === 'number' && !isNaN(payment)) {
        formik.setFieldValue('amortization.totalpaymentwillbe', payment.toFixed(2));
        formik.setFieldValue('amortization.noofpayments', payment.toFixed(2));
      } else {
        // Handle the case where payment is invalid
        formik.setFieldValue('amortization.totalpaymentwillbe', '0');
        formik.setFieldValue('amortization.noofpayments', '0');
      }
    } else {
      const totalPayment = parseFloat(paymentAmt);
      const rate = APR / intPP;
      const nper = calculateNPer(rate, totalPayment, PVal);
      payment = Math.ceil(nper);
      if (payment < 0) payment = 0;

      if (!isNaN(totalPayment)) {
        formik.setFieldValue('amortization.totalpaymentwillbe', totalPayment);
      }
      if (!isNaN(payment)) {
        formik.setFieldValue('amortization.noofpayments', payment);
      }

    }
  };

  useEffect(() => {
    if (formik.values.amortization?.paymentFrequency == 5) {
      setShowPaymentDates(true);
    } else {
      setShowPaymentDates(false);
    }
  }, [formik.values.amortization?.paymentFrequency]);


  useEffect(() => {
    if (
      formik.values.amortization.loanamount &&
      formik.values.amortization.interestrate &&
      formik.values.amortization.paymentFrequency
    ) {
      console.log('Recalculating payment...');
      recalculatePayment();
    }
  }, [
    formik.values.amortization.loanamount,
    formik.values.amortization.interestrate,
    formik.values.amortization.paymentFrequency,
    formik.values.amortization.paymentamount,
    formik.values.amortization.monthsamortized
  ]);


  const handleApprovalDateBlur = () => {
    formik.setFieldTouched("amortization.approvalDate", true, true);
  }

  const calcFirstDueDate = (approvalDate, paymentFrequency = formik.values.amortization.paymentFrequency) => {
    // 1 - Annual (4)
    // 2 - SemiAnnual (3)
    // 3 - Quarterly (2) ?
    // 12 - Monthly (1)
    // 24 - SemiMonthly  
    // 26 - Biweekly 
    // 52 - Weekly  
    console.log('ssssssssssssss');
    let resultDate;
    switch (paymentFrequency) {
      case 5:
        resultDate = approvalDate; // Empty string if strM is 24
        break;
      case 1:
        resultDate = approvalDate.add(1, 'month'); // Add 1 month
        break;
      case 6:
        resultDate = approvalDate.add(2, 'week'); // Add 2 weeks
        break;
      case 7:
        resultDate = approvalDate.add(1, 'week'); // Add 1 week
        break;
      case 2:
        resultDate = approvalDate.add(3, 'month'); // Add 1 quarter (3 months)
        break;
      case 3:
        resultDate = approvalDate.add(6, 'month'); // Add 6 months
        break;
      case 4:
        resultDate = approvalDate.add(1, 'year'); // Add 1 year
        break;
      default:
        resultDate = "";
    }

    // Set first due date to today + 1 month
    formik.setFieldValue('amortization.firstDueDate', resultDate);
    calcmaturityDate(resultDate);
  }

  const addQuarters = (date, quarters) => {
    return date.add(quarters * 3, 'month');
  };

  const updateBalloonDate = (newDate) => {
    //if (!BALLOON_DATE) { // Only update if it's not already set -- sri
    //console.log("UPDATED BALLOON DATE ");
    BALLOON_DATE = dayjs(newDate);
    //console.log(BALLOON_DATE);

    //}
  };

  const calcmaturityDate = (firstDueDate) => {
    console.log('inside mat date');
    let paymentPeriod = formik.values.amortization.paymentFrequency;
    let monthsAmortized = formik.values.amortization.monthsamortized;
    console.log(monthsAmortized);
    // srih
    let eachPay = formik.values.amortization.noofpayments;
    let approvalDate = formik.values.amortization.approvalDate;

    let mon = (!monthsAmortized || monthsAmortized === 0) ? eachPay : monthsAmortized;
    console.log('mon:', mon);
    let resultDate = dayjs(firstDueDate);

    switch (paymentPeriod) {

      case 1: // monthly 
        resultDate = resultDate.add(mon - 1, 'month');
        formik.setFieldValue('amortization.maturityDate', resultDate);
        updateBalloonDate(resultDate);
        break;

      case 5:

        const paymentDay1 = formik.values.amortization.paymentDate1; // e.g., 1
        const paymentDay2 = formik.values.amortization.paymentDate2; // e.g., 15

        let months = monthsAmortized > 0 ? monthsAmortized : (mon > 0 ? mon : 0);
        let totalPayments = months; //* 2;

        let semiDate = dayjs(firstDueDate);

        // Normalize firstDueDate to the nearest valid payment day
        if (semiDate.date() !== paymentDay1 && semiDate.date() !== paymentDay2) {
          if (semiDate.date() < paymentDay1) {
            semiDate = semiDate.date(paymentDay1);
          } else if (semiDate.date() < paymentDay2) {
            semiDate = semiDate.date(paymentDay2);
          } else {
            semiDate = semiDate.add(1, 'month').date(paymentDay1);
          }
        }

        let paymentsMade = 1;

        while (paymentsMade < totalPayments) {
          const currentDay = semiDate.date();

          if (currentDay === paymentDay1) {
            semiDate = semiDate.date(paymentDay2);
          } else {
            semiDate = semiDate.add(1, 'month').date(paymentDay1);
          }

          paymentsMade++;
        }

        // Ensure final payment is on paymentDay2
        resultDate = semiDate.date(paymentDay2);
        formik.setFieldValue('amortization.maturityDate', resultDate);
        updateBalloonDate(resultDate);
        break;

      case 6: // Biweekly
        if (monthsAmortized === 0) {
          resultDate = resultDate.add(mon * 2, 'week');
          //console.log(resultDate);
        } else {
          resultDate = resultDate.add(mon / 12 * 52 - 2, 'week');
        }
        formik.setFieldValue('amortization.maturityDate', resultDate);
        updateBalloonDate(resultDate);

        break;

      case 7: // weekly 
        if (monthsAmortized === 0) {
          resultDate = resultDate.add(mon - 1, 'week');
        } else {
          resultDate = resultDate.add(mon / 12 * 52 - 1, 'week');
        }
        formik.setFieldValue('amortization.maturityDate', resultDate);
        updateBalloonDate(resultDate);

        break;

      case 2: // quarterly 
        if (monthsAmortized === 0) {
          resultDate = addQuarters(resultDate, (mon) - 1)
        } else {
          resultDate = addQuarters(resultDate, mon / 3 - 1);
        }
        formik.setFieldValue('amortization.maturityDate', resultDate);
        updateBalloonDate(resultDate);

        break;

      case 3: // semi anually 
        if (monthsAmortized === 0) {
          resultDate = addQuarters(approvalDate, (mon * 2));
        } else {
          resultDate = addQuarters(approvalDate, mon / 3);
        }
        formik.setFieldValue('amortization.maturityDate', resultDate);
        updateBalloonDate(resultDate);

        break;

      case 4: // anually 
        if (monthsAmortized === 0) {
          resultDate = approvalDate.add(mon / 12, 'year');
        } else {
          resultDate = approvalDate.add(mon / 12, 'year');
        }
        formik.setFieldValue('amortization.maturityDate', resultDate);
        updateBalloonDate(resultDate);

        break;

      default:
        formik.setFieldValue('amortization.maturityDate', null);
        break;
    }
  }

  const handleApprovalDateChange = (newValue) => {
    const approvalDate = newValue ? newValue : null;
    formik.setFieldValue("amortization.approvalDate", approvalDate, true);

    if (!approvalDate) {
      formik.setFieldTouched("amortization.approvalDate", true, true);
      return;
    }

    calcFirstDueDate(approvalDate);
  }

  const handleFirstDueDateChange = (newValue) => {
    const firstduedate = newValue ? newValue : null;
    formik.setFieldValue("amortization.firstDueDate", firstduedate);
    calcmaturityDate(firstduedate);
  }

  const handleGenerateSchedule = async () => {
    try {
      // Validate the form
      const errors = await formik.validateForm();

      if (Object.keys(errors).length === 0) {
        const monthsamortized = parseCurrency(formik.values.amortization.monthsamortized);

        // Prepare updated amortization values
        const updatedAmortization = {
          ...formik.values.amortization,
          // Maturity can be shortened for a balloon loan, but the payment must
          // continue to use the original amortization term.
          monthsamortized,
          paymentDate1: formik.values.amortization?.paymentDate1,
          paymentDate2: formik.values.amortization?.paymentDate2,
        };

        console.log(updatedAmortization);
        // Prepare the full updated values object
        const updatedValues = {
          ...formik.values,
          amortization: updatedAmortization
        };

        // Calculate amortization schedule
        // updatedValues.paymentDate1 = values.amortization?.paymentDate1;
        // updatedValues.paymentDate2 = values.amortization?.paymentDate2;

        //console.log(updatedValues);

        const AmortizationRows = updatedAmortization.daily
          ? await calculateAmortizationDaily(updatedValues)
          : await calculateAmortization(updatedValues);

        // Update Formik with the new schedule list
        formik.setValues({
          ...updatedValues,
          amortization: {
            ...updatedAmortization,
            scheduleList: AmortizationRows
          },
          scheduleList: AmortizationRows
        });

        setRows(AmortizationRows);

        //console.log('Amortization schedule:', AmortizationRows);
      } else {
        // Set fields as touched to show validation errors
        formik.setTouched({
          amortization: {
            loanamount: true,
            monthsamortized: true,
            paymentamount: true,
            amor: true,
            interestrate: true,
            paymentFrequency: true,
            approvalDate: true,
            firstDueDate: true,
            maturityDate: true
          }
        });

        console.log('Validation errors:', errors);
      }
    } catch (error) {
      console.error('Error in handleGenerateSchedule:', error);
    }
  };


  const {
    rowModesModel,
    paymentFrequencyList,
    interestTypeList,
    interestPerYearList,
    loanFundList,
    handleAddFundingSource,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
    processRowUpdate,
    handleRowModesModelChange,
    calculateAmortization,
    calculateAmortizationDaily,
    AmortizationRows,
    pmt,
  } = useAmortization(
    initialRows,
    setRows,
    formik.values.amortization.scheduleList,
    formik.setFieldValue,
    rows,
    formik.values.amortization.interestrate
  );

  const paymentFrequencyOptions = useMemo(() => paymentFrequencyList
    .map((item) => {
      const paymentsPerYear = PAYMENT_FREQUENCY_COUNTS[item.id];
      const name = String(item.name || '').replace(/^\d+\s*/, '');

      return {
        ...item,
        paymentsPerYear,
        label: paymentsPerYear ? `${paymentsPerYear} ${name}` : name,
      };
    })
    .sort((a, b) => (a.paymentsPerYear || Number.MAX_SAFE_INTEGER) - (b.paymentsPerYear || Number.MAX_SAFE_INTEGER)),
  [paymentFrequencyList]);

  const selectPaymentFrequency = (option) => {
    formik.setFieldValue('amortization.paymentFrequency', option?.id ?? null);
    setShowPaymentDates(option?.id === 5);

    const approvalDate = dayjs(formik.values.amortization.approvalDate);
    if (option && approvalDate.isValid()) {
      calcFirstDueDate(approvalDate, option.id);
    }
  };

  const handlePaymentFrequencyInputChange = (_event, inputValue, reason) => {
    if (reason !== 'input') return;

    clearTimeout(paymentFrequencyTypingTimer.current);
    const typedNumber = inputValue.trim();
    if (!/^\d+$/.test(typedNumber)) return;

    const matchingOption = paymentFrequencyOptions.find(
      (option) => String(option.paymentsPerYear) === typedNumber
    );

    if (matchingOption) {
      paymentFrequencyTypingTimer.current = setTimeout(() => {
        selectPaymentFrequency(matchingOption);
      }, 300);
    }
  };

  const scheduleTotals = useMemo(() => (rows || []).reduce((totals, row) => ({
    payment: totals.payment + (parseCurrency(row.payment) || 0),
    principal: totals.principal + (parseCurrency(row.principal) || 0),
    interest: totals.interest + (parseCurrency(row.interest) || 0),
    accruedInterest: totals.accruedInterest + (parseCurrency(row.accruedinterest) || 0),
  }), {
    payment: 0,
    principal: 0,
    interest: 0,
    accruedInterest: 0,
  }), [rows]);

  const columns = [
    {
      field: 'Pay',
      headerName: 'Pay',
      width: 120,
      editable: false,
    },
    {
      field: 'duedate',
      headerName: 'Due Date',
      width: 120,
      editable: !readOnly,
      renderEditCell: (params) => (
        <DatePicker
          value={params.value}
          onChange={(newValue) => params.api.setEditCellValue({ id: params.id, field: params.field, value: formatDate(newValue) })}
        />
      ),
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 120,
      editable: true,
      valueFormatter: (params) => {
        let value = params;

        if (value === undefined || value === null) {
          value = params.row ? params.row[params.field] : undefined;
        }

        if (value == null || value === '') return '';

        const number = Number(value);
        if (isNaN(number)) return value;

        return number.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      field: 'payment',
      headerName: 'Payment',
      width: 120,
      editable: true,
      valueFormatter: (params) => {
        let value = params;

        if (value === undefined || value === null) {
          value = params.row ? params.row[params.field] : undefined;
        }

        if (value == null || value === '') return '';

        const number = Number(value);
        if (isNaN(number)) return value;

        return number.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      field: 'principal',
      headerName: 'Principal',
      width: 120,
      editable: true,
      valueFormatter: (params) => {
        let value = params;

        if (value === undefined || value === null) {
          value = params.row ? params.row[params.field] : undefined;
        }

        if (value == null || value === '') return '';

        const number = Number(value);
        if (isNaN(number)) return value;

        return number.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      field: 'interest',
      headerName: 'Interest',
      width: 120,
      editable: true,
      valueFormatter: (params) => {
        let value = params;

        if (value === undefined || value === null) {
          value = params.row ? params.row[params.field] : undefined;
        }

        if (value == null || value === '') return '';

        const number = Number(value);
        if (isNaN(number)) return value;

        return number.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      field: 'accruedinterest',
      headerName: 'Accrued Interest',
      width: 120,
      editable: false,
      valueFormatter: (params) => {
        let value = params;

        if (value === undefined || value === null) {
          value = params.row ? params.row[params.field] : undefined;
        }

        if (value == null || value === '') return '';

        const number = Number(value);
        if (isNaN(number)) return value;

        return number.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 0.3,
      width: 150,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{ color: 'primary.main' }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={handleEditClick(id)}
            color="primary"
          />,
          // <GridActionsCellItem
          //   icon={<DeleteIcon />}
          //   label="Delete"
          //   onClick={handleDeleteClick(id)}
          //   color="error"
          // />,
        ];
      }
    }
  ];

  // Disable payment amount input if months amortized is not 0
  const [isPaymentAmountDisabled, setIsPaymentAmountDisabled] = useState(false);
  //const currencyFieldProps = useCurrencyField('amortization.paymentamount', formik.values.amortization.paymentamount)(formik);

  useEffect(() => {
    const monthsAmortized = Number(formik.values.amortization.monthsamortized);
    const shouldDisable = monthsAmortized !== 0;

    setIsPaymentAmountDisabled(shouldDisable);

    if (shouldDisable) {
      // Reset the payment amount if the field is being disabled
      formik.setFieldValue('amortization.paymentamount', 0); // or null if that's preferred
    }
  }, [formik.values.amortization.monthsamortized]);
  //const currencyProps = useCurrencyField('amortization.loanamount', loanTerms.loanAmount);

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Box p={3}>
          <Typography variant="h6">Amortization</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item md={2} sm={4} xs={12}>
              Loan Amount
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
              <StyledInput
                size="small"
                name="amortization.loanamount"
                label="Loan Amount"
                // value={formik.values.amortization.loanamount}
                // onBlur={formik.handleBlur}
                // onChange={formik.handleChange}
                //{...useCurrencyField('amortization.loanamount', loanTerms.loanAmount)}
                //sri{...useCurrencyField('loanTerms.loanAmount')}
                {...useCurrencyFieldAmor(formik, 'amortization.loanamount')}
                //{...currencyProps}
                //{...useCurrencyFieldAmor(formik, 'amortization.loanAmount')}

                error={Boolean(formik.touched.amortization?.loanamount && formik.errors.amortization?.loanamount)}
                helperText={formik.touched.amortization?.loanamount && formik.errors.amortization?.loanamount}
              />

            </Grid>
            <Grid item md={2} sm={4} xs={12}>
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
            </Grid>
            {/* second row  */}
            <Grid item md={2} sm={4} xs={10}>
              Months Amortized
            </Grid>
            <Grid item md={3} sm={6} xs={10}>
              <StyledInput
                size="small"
                name="amortization.monthsamortized"
                label="Months Amortized"
                InputProps={{ readOnly: readOnly }}
                value={formik.values.amortization.monthsamortized}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.amortization?.monthsamortized && formik.errors.amortization?.monthsamortized)}
                helperText={formik.touched.amortization?.monthsamortized && formik.errors.amortization?.monthsamortized}
              />
            </Grid>
            <Grid item md={1} sm={2} xs={12}>
              or Payment Amount
            </Grid>
            <Grid item md={3} sm={6} xs={12}>
              <StyledInput
                size="small"
                name="amortization.paymentamount"
                label="Payment Amount"
                InputProps={{ readOnly: readOnly }}
                // value={formik.values.amortization.paymentamount}
                // onBlur={formik.handleBlur}
                // onChange={formik.handleChange}
                //{...useCurrencyField('amortization.paymentamount')}
                {...useCurrencyFieldAmor(formik, 'amortization.paymentamount')}
                error={Boolean(formik.touched.amortization?.paymentamount && formik.errors.amortization?.paymentamount)}
                helperText={formik.touched.amortization?.paymentamount && formik.errors.amortization?.paymentamount}
                style={{ width: '100px' }}
                disabled={isPaymentAmountDisabled}
              />
            </Grid>
            <Grid item md={3} sm={6} xs={12}>

            </Grid>
            {/* Third row  */}
            <Grid item md={2} sm={4} xs={10}>
              Interest Rate %
            </Grid>
            <Grid item md={3} sm={6} xs={10}>
              <StyledInput
                size="small"
                name="amortization.interestrate"
                label="Interest Rate"
                InputProps={{ readOnly: readOnly }}
                value={formik.values.amortization.interestrate}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.amortization?.interestrate && formik.errors.amortization?.interestrate)}
                helperText={formik.touched.amortization?.interestrate && formik.errors.amortization?.interestrate}
              />
            </Grid>
            <Grid item md={1} sm={2} xs={12}>
              Payment per Year
            </Grid>
            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={paymentFrequencyOptions}
                value={paymentFrequencyOptions.find(
                  (option) => Number(option.id) === Number(formik.values.amortization.paymentFrequency)
                ) || null}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                onChange={(_event, option) => {
                  clearTimeout(paymentFrequencyTypingTimer.current);
                  selectPaymentFrequency(option);
                }}
                onInputChange={handlePaymentFrequencyInputChange}
                filterOptions={(options, { inputValue }) => {
                  const query = inputValue.trim().toLowerCase();
                  if (!query) return options;

                  return options.filter((option) => (
                    String(option.paymentsPerYear).startsWith(query) ||
                    option.label.toLowerCase().includes(query)
                  ));
                }}
                autoHighlight
                autoSelect
                openOnFocus
                readOnly={readOnly}
                sx={{ minWidth: 233 }}
                renderInput={(params) => (
                  <StyledInput
                    {...params}
                    name="amortization.paymentFrequency"
                    label="Payment Frequency"
                    onBlur={(e) => handlePaymentFrequencyBlur(e, handleBlur)}
                    error={Boolean(formik.touched.amortization?.paymentFrequency && formik.errors.amortization?.paymentFrequency)}
                    helperText={formik.touched.amortization?.paymentFrequency && formik.errors.amortization?.paymentFrequency}
                    size="small"
                    inputProps={{
                      ...params.inputProps,
                      inputMode: 'numeric',
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item md={2} sm={4} xs={12}>

              {showPaymentDates && (
                <>
                  {/* <Grid item md={2} sm={4} xs={12}>
                                        Payment Dates
                                    </Grid> */}
                  <Grid item md={2} sm={4} xs={12}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <SmallStyledInput
                        size="small"
                        name="amortization.paymentDate1"
                        label="Date 1"
                        //value={values.loanTerms?.paymentDate1}
                        //onChange={(e) => formik.setFieldValue('loanTerms.paymentDate1', e.target.value)}
                        value={formik.values.amortization.paymentDate1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        // error={
                        //     formik.touched.amortization?.paymentDate1 &&
                        //     Boolean(formik.errors.amortization?.paymentDate1)
                        // }
                        // helperText={
                        //     formik.touched.amortization?.paymentDate1 &&
                        //     formik.errors.amortization?.paymentDate1
                        // }
                        error={Boolean(formik.touched.amortization?.paymentDate1 && formik.errors.amortization?.paymentDate1)}
                        helperText={formik.touched.amortization?.paymentDate1 && formik.errors.amortization?.paymentDate1}

                        style={{ marginRight: '14px' }}
                        InputProps={{ readOnly: readOnly }}
                      />

                      <SmallStyledInput
                        size="small"
                        name="amortization.paymentDate2"
                        label="Date 2"
                        //value={values.loanTerms?.paymentDate2}
                        //onChange={(e) => formik.setFieldValue('loanTerms.paymentDate2', e.target.value)}
                        value={formik.values.amortization.paymentDate2}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.amortization?.paymentDate2 &&
                          Boolean(formik.errors.amortization?.paymentDate2)
                        }
                        helperText={
                          formik.touched.amortization?.paymentDate2 &&
                          formik.errors.amortization?.paymentDate2
                        }
                        InputProps={{ readOnly: readOnly }}
                      />
                    </Box>
                  </Grid>
                </>
              )}

            </Grid>

            <Grid item md={2} sm={4} xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.amortization?.amor}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      formik.setFieldValue("amortization.amor", isChecked);
                      if (isChecked) {
                        formik.setFieldValue("amortization.daily", false); // Uncheck Daily if Amortized is checked
                      }
                    }}
                    //onChange={formik.handleChange}
                    name="amortization.amor"
                    disabled={readOnly}
                  />
                }
                label="Amortized"
              />
            </Grid>
            <Grid item md={5} sm={8} xs={12}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: 40,
                  px: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, mr: formik.values.amortization?.daily ? 1 : 0 }}
                  control={
                    <Checkbox
                      checked={formik.values.amortization?.daily}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        formik.setFieldValue("amortization.daily", isChecked);
                        if (isChecked) {
                          formik.setFieldValue("amortization.dailyValues", "365");  // Set dailyValues to 365 when Daily is checked
                          formik.setFieldValue("amortization.amor", false); // Uncheck Amortized if Daily is checked
                        } else {
                          formik.setFieldValue("amortization.dailyValues", null); // Reset or leave empty when unchecked
                        }
                      }}
                      name="amortization.daily"
                      disabled={readOnly}
                    />
                  }
                  label="Daily"
                />
                {formik.values.amortization?.daily && (
                  <RadioGroup
                    value={formik.values.amortization?.dailyValues}
                    onChange={formik.handleChange}
                    name="amortization.dailyValues"
                    row
                    sx={{ flexWrap: 'nowrap', gap: 0.5 }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, mr: 0.5 }}
                      value="365"
                      control={<Radio size="small" disabled={readOnly} />}
                      label="365"
                    />
                    <FormControlLabel
                      sx={{ m: 0 }}
                      value="360"
                      control={<Radio size="small" disabled={readOnly} />}
                      label="360"
                    />
                  </RadioGroup>
                )}
              </Box>
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
            </Grid>

            {/* fourth row  */}
            <Grid item md={2} sm={4} xs={12}>
              {formik.values.amortization.monthsamortized
                ? 'Each Payment will be'
                : 'No of Payments will be'}
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
              <StyledInput
                size="small"
                name="amortization.noofpayments"
                label="No of Payments will be"
                InputProps={{ readOnly: true }}
                value={formik.values.amortization.noofpayments ?? 0}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.amortization?.noofpayments && formik.errors.amortization?.noofpayments)}
                helperText={formik.touched.amortization?.noofpayments && formik.errors.amortization?.noofpayments}
              />
            </Grid>
            <Grid item md={3} sm={4} xs={12}>
            </Grid>
            <Grid item md={3} sm={8} xs={12}>

            </Grid>
            {/* fifth row  */}
            <Grid item md={12} sm={12} xs={12}>
              <Box
                sx={{
                  position: 'relative',
                  my: 2,
                  height: 2,
                  background: 'linear-gradient(135deg, #e0e0e0, #bdbdbd)',
                  borderRadius: 1,
                  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                }}
              />
            </Grid>
            <Grid item md={2} sm={4} xs={12}>
              Additional Service Fee
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
              <StyledInput
                size="small"
                name="amortization.additionalsvcfee"
                label="Additional Service Fee"
                InputProps={{ readOnly: readOnly }}
                value={formik.values.amortization.additionalsvcfee}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.amortization?.additionalsvcfee && formik.errors.amortization?.additionalsvcfee)}
                helperText={formik.touched.amortization?.additionalsvcfee && formik.errors.amortization?.additionalsvcfee}

              />
            </Grid>
            <Grid item md={2} sm={4} xs={12}>
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
            </Grid>
            {/* Sixth row  */}
            <Grid item md={2} sm={4} xs={12}>
              Total Payment will be
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
              <StyledInput
                size="small"
                name="amortization.totalpaymentwillbe"
                label="Total Payment will be"
                InputProps={{ readOnly: true }}
                // value={formik.values.amortization.totalpaymentwillbe}
                // onBlur={formik.handleBlur}
                // onChange={formik.handleChange}
                //{...useCurrencyField('amortization.totalpaymentwillbe')}
                {...useCurrencyFieldAmor(formik, 'amortization.totalpaymentwillbe')}
                error={Boolean(formik.touched.amortization?.totalpaymentwillbe && formik.errors.amortization?.totalpaymentwillbe)}
                helperText={formik.touched.amortization?.totalpaymentwillbe && formik.errors.amortization?.totalpaymentwillbe}
              />
            </Grid>
            <Grid item md={2} sm={4} xs={12}>
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
            </Grid>
            {/* Seventh row  */}
            <Grid item md={12} sm={12} xs={12}>
              <Box
                sx={{
                  position: 'relative',
                  my: 2,
                  height: 2,
                  background: 'linear-gradient(135deg, #e0e0e0, #bdbdbd)',
                  borderRadius: 0,
                  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                }}
              />
            </Grid>
            <Grid item md={2} sm={4} xs={12}>
              Approval Date
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  name='amortization.approvalDate'
                  value={formik.values.amortization?.approvalDate ? dayjs(formik.values.amortization?.approvalDate) : null}
                  onClose={() => {
                    handleApprovalDateBlur();
                  }}
                  onChange={(newValue) => {
                    handleApprovalDateChange(newValue)
                  }}
                  slotProps={{
                    textField: {
                      error: Boolean(isDateError('approvalDate')),
                      helperText: isDateError('approvalDate'),
                      label: "Approval Date",
                      size: "small",
                    }
                  }}
                  disabled={readOnly}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item md={2} sm={4} xs={12}>
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
            </Grid>
            {/* 8th row  */}
            <Grid item md={2} sm={4} xs={12}>
              First Due Date
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  value={formik.values.amortization?.firstDueDate ? dayjs(formik.values.amortization?.firstDueDate) : null}
                  //value={loanTerms.firstDueDate ? dayjs(loanTerms.firstDueDate) : null}
                  onChange={(newValue) => {
                    handleFirstDueDateChange(newValue)
                  }}
                  slotProps={{
                    textField: {
                      error: isDateError('firstDueDate'),
                      helperText: isDateError('firstDueDate'),
                      label: "First Due Date",
                      size: "small",
                    }
                  }}
                  disabled={readOnly}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item md={2} sm={4} xs={12}>
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
            </Grid>
            {/* 9th row  */}
            <Grid item md={2} sm={4} xs={12}>
              Maturity Date
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  value={formik.values.amortization?.maturityDate ? dayjs(formik.values.amortization?.maturityDate) : null}
                  onChange={(newValue) => {
                    formik.setFieldValue("amortization.maturityDate", dayjs(newValue));
                  }}
                  slotProps={{
                    textField: {
                      error: isDateError('maturityDate'),
                      helperText: isDateError('maturityDate'),
                      label: "Closing Date",
                      size: "small",
                    }
                  }}
                  disabled={readOnly}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item md={2} sm={4} xs={12}>
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
            </Grid>
            <Grid item md={2} sm={4} xs={12}>
            </Grid>
            <Grid item md={4} sm={8} xs={12}>
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box mt={3}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h6">Amortization Schedule</Typography>
                  <Button
                    color="primary"
                    startIcon={<AddCircleIcon />}
                    onClick={handleGenerateSchedule}
                    disabled={readOnly}
                  >
                    Generate Schedule
                  </Button>
                  <PrintButton
                    printServiceFunction={printAmortizationReport}
                    params={[formik.values.amortization, rows]}
                    disabled={!rows?.length}
                    reportTitle="Amortization Report"
                    buttonText="Print"
                  />
                </Box>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={5}
                  rowsPerPageOptions={[5, 10, 20]}
                  autoHeight
                  disableSelectionOnClick
                  editMode="row"
                  rowModesModel={rowModesModel}
                  onRowModesModelChange={handleRowModesModelChange}
                  onRowEditStop={handleRowEditStop}
                  //processRowUpdate={processRowUpdate}
                  processRowUpdate={(newRow) => processRowUpdate(newRow, formik.values.amortization.paymentFrequency)}
                />
                <Box sx={{ overflowX: 'auto' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 990,
                      minHeight: 48,
                      border: '1px solid',
                      borderTop: 0,
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Typography sx={{ width: 360, flexShrink: 0, px: 1.25 }} fontWeight={700}>
                      Loan Totals
                    </Typography>
                    {[
                      ['payment', scheduleTotals.payment],
                      ['principal', scheduleTotals.principal],
                      ['interest', scheduleTotals.interest],
                      ['accruedInterest', scheduleTotals.accruedInterest],
                    ].map(([key, total]) => (
                      <Typography
                        key={key}
                        sx={{ width: 120, flexShrink: 0, px: 1.25, textAlign: 'left' }}
                        fontWeight={700}
                      >
                        {formatCurrency(total)}
                      </Typography>
                    ))}
                    <Box sx={{ minWidth: 150, flex: 0.3 }} />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

      </Form>
    </FormikProvider >
  );
};

export default Amortization;
